/**
 * Folgeunterlagen aus der Planungsphase — Auswahl und Anlage (Dienst).
 *
 * Ist die Planungsphase erfuellt, bietet das Modul an, SiGe-Plan, Handbuch,
 * Baustellenordnung und A+S-Plan als ENTWURF anzulegen (beim Abschluss oder
 * danach in der Karte „Folgeunterlagen"). Die Erzeuger selbst stehen in
 * `*Entwurf.ts` (Vertrag `typen.ts`); hier stehen nur
 *
 *   folgeAuswahl            welche Unterlage vorgewaehlt und waehlbar ist —
 *                           pur, aus Pflichtenstand und gelesenem Bestand;
 *   leseBestaende           die vier Bestaende lesen (liest nur, darf parallel);
 *   legeFolgeunterlagenAn   die gewaehlten STRENG NACHEINANDER in
 *                           `FOLGE_REIHENFOLGE` anlegen.
 *
 * ═══ HAUSREGEL ════════════════════════════════════════════════════════════
 * Die Vorauswahl ist dreiwertig wie die Pflichten: `pflicht` und
 * `ungeklaert` vorgewaehlt (im Zweifel lieber ein Entwurf zu viel als eine
 * fehlende Unterlage), `keine` nicht vorgewaehlt, aber waehlbar. Was es schon
 * gibt, ist nicht waehlbar — die Erzeuger ueberschreiben nie; dort zeigt die
 * Leiste „Aus der Planungsphase" im Modul die Vorschlaege wie bisher.
 *
 * Nacheinander, weil der SiGe-Plan keinen Unique-Index je Projekt hat (nur
 * Bestandspruefung und Laufsperre verhindern die Doppelanlage) und das
 * Handbuch seine Gewerke aus dem eben angelegten SiGe-Plan ableitet. Ein
 * Fehler bricht die uebrigen nicht ab; ein Wurf eines Erzeugers wird zu
 * `{ status: 'fehler' }`.
 *
 * Nur-Ansicht schliesst der Aufrufer aus (`useFolgeunterlagen`, Container).
 */

import { describeError } from '../errorMessage';
import type { PflichtArt, PflichtEintrag, PflichtStand } from '../../components/planungsphase/types';
import { asplanErzeuger } from './asplanEntwurf';
import { bauordnungErzeuger } from './bauordnungEntwurf';
import { handbuchErzeuger } from './handbuchEntwurf';
import { sigeplanErzeuger } from './sigeplanEntwurf';
import {
    FOLGE_REIHENFOLGE,
    type BestandStand,
    type ErzeugungsErgebnis,
    type FolgeArt,
    type FolgeErzeuger,
    type PlanungsQuelle,
} from './typen';

/** Die vier Erzeuger je Art. */
export const ERZEUGER: Record<FolgeArt, FolgeErzeuger> = {
    sigeplan: sigeplanErzeuger,
    handbuch: handbuchErzeuger,
    bauordnung: bauordnungErzeuger,
    asplan: asplanErzeuger,
};

/** Gelesene Bestaende; eine fehlende Art ist noch nicht gelesen. */
export type FolgeBestaende = Partial<Record<FolgeArt, BestandStand>>;

/**
 * Welche Pflicht ueber eine Folgeunterlage entscheidet. Handbuch und
 * Baustellenordnung haben keine eigene Pflicht nach BaustellV — sie sind
 * Werkzeuge des Koordinators und haengen deshalb an dessen Pflicht.
 */
export const PFLICHT_JE_FOLGE: Record<FolgeArt, PflichtArt> = {
    sigeplan: 'sigeplan',
    handbuch: 'koordinator',
    bauordnung: 'koordinator',
    asplan: 'asplan',
};

/** Wovon der Satz „… erforderlich" spricht. */
const PFLICHT_GEGENSTAND: Partial<Record<PflichtArt, string>> = {
    sigeplan: 'SiGe-Plan',
    koordinator: 'Koordinator',
    asplan: 'A+S-Plan',
};

export const BESTAND_GESPERRT_TEXT = 'besteht bereits, Vorschläge im Modul unter „Aus der Planungsphase“';
export const BESTAND_PRUEFT_TEXT = 'wird geprüft …';

export interface FolgeAuswahlZeile {
    art: FolgeArt;
    /** Titel des Erzeugers („SiGe-Plan", „Handbuch der Koordinierung" …). */
    titel: string;
    pflicht: PflichtStand;
    /** Kurzer Satz aus dem Pflichtstand („SiGe-Plan erforderlich (§ 2 Abs. 3 BaustellV)"). */
    grund: string;
    /** Begruendung des Pflichtenstands (bei `ungeklaert`: was noch fehlt) — fuer den Tooltip. */
    begruendung: string;
    /** `null` = noch nicht gelesen. */
    bestand: BestandStand | null;
    /** Stand des Bestands als Satz fuer die Karte („besteht: Entwurf vom …", „noch nicht angelegt"). */
    bestandText: string;
    /** Nur `waehlbar` wird je vorgewaehlt. */
    vorgewaehlt: boolean;
    waehlbar: boolean;
    /** Warum nicht waehlbar — `null`, wenn waehlbar. */
    sperrgrund: string | null;
}

const grundAus = (eintrag: PflichtEintrag | undefined, art: FolgeArt): { stand: PflichtStand; grund: string; begruendung: string } => {
    if (!eintrag) {
        return { stand: 'ungeklaert', grund: 'Pflicht noch nicht geklärt', begruendung: 'Der Pflichtenstand führt diese Unterlage nicht.' };
    }
    const begruendung = eintrag.begruendung || '';
    if (eintrag.stand === 'pflicht') {
        const gegenstand = PFLICHT_GEGENSTAND[PFLICHT_JE_FOLGE[art]] || 'Unterlage';
        const recht = (eintrag.rechtsgrundlage || '').trim();
        return { stand: 'pflicht', grund: recht ? `${gegenstand} erforderlich (${recht})` : `${gegenstand} erforderlich`, begruendung };
    }
    if (eintrag.stand === 'keine') return { stand: 'keine', grund: 'nach den Angaben nicht erforderlich', begruendung };
    return { stand: 'ungeklaert', grund: 'Pflicht noch nicht geklärt', begruendung };
};

const bestandSaetze = (bestand: BestandStand | null): { bestandText: string; sperrgrund: string | null } => {
    if (!bestand) return { bestandText: BESTAND_PRUEFT_TEXT, sperrgrund: BESTAND_PRUEFT_TEXT };
    if (bestand.status === 'keiner') return { bestandText: 'noch nicht angelegt', sperrgrund: null };
    if (bestand.status === 'vorhanden') {
        return { bestandText: `besteht: ${bestand.beschreibung || 'vorhanden'}`, sperrgrund: BESTAND_GESPERRT_TEXT };
    }
    const fehler = bestand.fehler || 'unbekannter Fehler';
    return { bestandText: `nicht lesbar: ${fehler}`, sperrgrund: `Bestand nicht lesbar: ${fehler}` };
};

/**
 * Die vier Zeilen der Auswahl in `FOLGE_REIHENFOLGE` — pur.
 *
 * Waehlbar nur, wenn der Bestand gelesen ist und es keinen gibt (`keiner`).
 * Vorgewaehlt nur, was waehlbar ist und nicht ausdruecklich `keine` Pflicht
 * hat.
 */
export const folgeAuswahl = (
    pflichten: readonly PflichtEintrag[] | null | undefined,
    bestaende: FolgeBestaende | null | undefined,
): FolgeAuswahlZeile[] =>
    FOLGE_REIHENFOLGE.map(art => {
        const eintrag = (pflichten || []).find(p => p.art === PFLICHT_JE_FOLGE[art]);
        const { stand, grund, begruendung } = grundAus(eintrag, art);
        const bestand = (bestaende && bestaende[art]) || null;
        const { bestandText, sperrgrund } = bestandSaetze(bestand);
        const waehlbar = !!bestand && bestand.status === 'keiner';
        return {
            art,
            titel: ERZEUGER[art].titel,
            pflicht: stand,
            grund,
            begruendung,
            bestand,
            bestandText,
            vorgewaehlt: waehlbar && stand !== 'keine',
            waehlbar,
            sperrgrund,
        };
    });

/**
 * Liest die vier Bestaende (parallel — Lesen legt nichts an). Ein Wurf wird
 * zu `{ status: 'fehler' }`; die Funktion lehnt nie ab.
 */
export const leseBestaende = async (
    projectId: string,
    erzeuger: Record<FolgeArt, FolgeErzeuger> = ERZEUGER,
): Promise<FolgeBestaende> => {
    const staende = await Promise.all(FOLGE_REIHENFOLGE.map(async (art): Promise<BestandStand> => {
        try {
            return await erzeuger[art].bestand(projectId);
        } catch (e: unknown) {
            return { status: 'fehler', fehler: `${erzeuger[art].titel}: ${describeError(e)}` };
        }
    }));
    const aus: FolgeBestaende = {};
    FOLGE_REIHENFOLGE.forEach((art, i) => { aus[art] = staende[i]; });
    return aus;
};

/** Satz ueber nicht lesbare Bestaende — `null`, wenn alles gelesen ist. */
export const lesefehlerText = (
    bestaende: FolgeBestaende | null | undefined,
    erzeuger: Record<FolgeArt, FolgeErzeuger> = ERZEUGER,
): string | null => {
    if (!bestaende) return null;
    const titel = FOLGE_REIHENFOLGE.filter(art => bestaende[art]?.status === 'fehler').map(art => erzeuger[art].titel);
    if (!titel.length) return null;
    return titel.length === 1
        ? `Der Bestand von „${titel[0]}“ konnte nicht gelesen werden.`
        : `Der Bestand von ${titel.map(t => `„${t}“`).join(', ')} konnte nicht gelesen werden.`;
};

/**
 * Legt die gewaehlten Unterlagen STRENG NACHEINANDER in `FOLGE_REIHENFOLGE`
 * an — die Reihenfolge von `arten` zaehlt nicht, doppelte Eintraege zaehlen
 * einmal. Ein Fehler bricht die uebrigen nicht ab; ein Wurf eines Erzeugers
 * wird zu `{ status: 'fehler' }`. Lehnt nie ab.
 */
export const legeFolgeunterlagenAn = async (
    arten: readonly FolgeArt[],
    q: PlanungsQuelle,
    erzeuger: Record<FolgeArt, FolgeErzeuger> = ERZEUGER,
): Promise<ErzeugungsErgebnis[]> => {
    const gewaehlt = new Set(arten);
    const ergebnisse: ErzeugungsErgebnis[] = [];
    for (const art of FOLGE_REIHENFOLGE) {
        if (!gewaehlt.has(art)) continue;
        try {
            ergebnisse.push(await erzeuger[art].legeAn(q));
        } catch (e: unknown) {
            ergebnisse.push({
                status: 'fehler',
                art,
                fehler: `${erzeuger[art].titel} konnte nicht angelegt werden: ${describeError(e)}`,
            });
        }
    }
    return ergebnisse;
};
